import '../../datatables/tags-sort';
import I18n from 'i18n-js/index.js.erb';
import sweetAlert from 'sweetalert';
import displayDatatablesPagination from '../shared/datatables_pagination';
import { withSaveState } from '../shared/datatables_state';
import {
  overridePaginationClasses,
  customizeDOMPaginationClasses
} from '../../datatables/customization-v2';
import {
  setup as tableSetup,
  setupPaginationOnDOM,
  customizePageLength,
  exportAllMatchingRows,
  defaultTableOptions
} from '../../datatables/table-setup';
import SidePanel from './sidepanel';
import TableRowActive from './table_row_active';
import ToggleVisible from './toggle_visible';
import ProgressDialog from './progress_dialog';

const columnRenders = {
  check: (data) => {
    // With server-side paging the checkbox column resolves `RowData.pkey`
    // directly (see EventPartTableColumns#checkbox_column), so `data` is the id.
    const id = data;
    return `
      <input data-id="${id}" id="check_${id}" class="checkbox table__checkbox" type="checkbox">
      <label for="check_${id}" class="table__label js-checkbox-for-row no-space-out voidclick"></label>`;
  },
  boolean: (data) => {
    let normalizedData = data === null ? '' : data;
    normalizedData = normalizedData.toString().toLowerCase();
    if (normalizedData === 'yes' || normalizedData === 'true') {
      return 'Yes';
    }
    if (normalizedData === 'no' || normalizedData === 'false') {
      return 'No';
    }
    return '';
  },
  createAttendingRender: defaultResponse => (data, type, full, meta) => {
    const normalizedData = data === null ? '' : data;
    if (normalizedData !== '') {
      return columnRenders.boolean(data, type, full, meta);
    }
    return defaultResponse;
  }
};

const TrackResponsesTable = {
  start() {
    const $table = $('.js-track-responses-table:not(.dataTable)');
    if (!$table.length) {
      return;
    }
    // selectedTagIds is shared between the DT ext.search filter, the
    // state save/load listeners (bound before .DataTable() in setupDataTable)
    // and the watchTagFilter change handlers.
    const selectedTagIds = new Set();
    // Persisted row selection (list_item ids), kept across server-side page
    // changes so bulk actions can operate on the full set, not just the
    // visible page.
    const selection = this._buildSelection($table);
    this.setupDataTable($table, selectedTagIds, selection);
    this.watchRowClick($table);
    const datatable = $table.DataTable();
    selection.bind(datatable);
    this.setupExport($table, datatable);
    $table.on('datatable:deselect', () => selection.clear(datatable));
    $table.on('datatable:reload', () => datatable.ajax.reload());
    this.watchTagFilter(datatable, $table, selectedTagIds);
    this.watchProcessingMessage($table);
  },

  // Sorting/searching a dynamic (custom field / survey) column can take a few
  // seconds on large groups. Reuse the DataTables processing overlay (already
  // shown during every server request) but give it a contextual message so the
  // organizer knows work is happening. Fast native operations just flash it.
  watchProcessingMessage($table) {
    let previousSearch = '';
    let previousOrder = '';
    let message = null;
    $table.on('preXhr.dt', (e, settings, data) => {
      const search = (data.search && data.search.value) || '';
      const order = JSON.stringify(data.order || []);
      if (search !== previousSearch) {
        message = I18n.t('datatables.searching');
      } else if (order !== previousOrder) {
        message = I18n.t('datatables.sorting');
      } else {
        message = null; // paging — keep the default processing label
      }
      previousSearch = search;
      previousOrder = order;
    });
    $table.on('processing.dt', (e, settings, processing) => {
      if (processing && message) {
        $('.dataTables_processing', settings.nTableWrapper).text(message);
      }
    });
  },

  // Live row-count subtitle under the table title ("1999 People"), reusing the
  // Track Overview people_count / places_count copy. recordsDisplay reflects the
  // current search/filter, so the count updates on every draw.
  _renderCount($table, info) {
    const key = $table.data('countI18nKey');
    if (!key) {
      return;
    }
    $('.js-track-responses-count').text(I18n.t(key, { count: info.recordsDisplay }));
  },

  watchTagFilter(table, $table, selectedTagIds) {
    $('.js-tag-filter-dropdown .dropdown__list').on('click', (e) => {
      e.stopPropagation();
    });

    // Tag filtering is applied server-side (tag_ids injected via ajax.data),
    // so a selection change reloads the current page from the server rather
    // than re-running a client-side predicate over the loaded rows.
    $('.js-tag-filter-check').on('change', (e) => {
      const tagId = String($(e.currentTarget).data('tag-id'));
      if (e.currentTarget.checked) {
        selectedTagIds.add(tagId);
      } else {
        selectedTagIds.delete(tagId);
      }
      this._updateTagFilterUI(selectedTagIds.size);
      table.state.save();
      table.ajax.reload();
    });

    $('.js-tag-filter-clear').on('click', (e) => {
      e.preventDefault();
      selectedTagIds.clear();
      $('.js-tag-filter-check').prop('checked', false);
      this._updateTagFilterUI(0);
      table.state.save();
      table.ajax.reload();
    });
  },

  _updateTagFilterUI(count) {
    $('.js-tag-filter-counter').text(count).toggleClass('is-hidden', count === 0);
    $('.js-tag-filter-clear').toggleClass('is-hidden', count === 0);
  },

  setupDataTable($table, selectedTagIds, selection) {
    overridePaginationClasses();
    let table;

    // Persist the tag filter selection via DataTables' save/load state channel.
    // Bind before .DataTable() so the initial stateLoadParams event during
    // init is caught and we can restore checkboxes before the first draw.
    $table.on('stateSaveParams.dt', (e, settings, data) => {
      data.selectedTagIds = Array.from(selectedTagIds);
    });
    $table.on('stateLoadParams.dt', (e, settings, data) => {
      if (!Array.isArray(data.selectedTagIds) || data.selectedTagIds.length === 0) return;
      selectedTagIds.clear();
      data.selectedTagIds.forEach(id => selectedTagIds.add(String(id)));
      selectedTagIds.forEach(id => {
        $(`.js-tag-filter-check[data-tag-id="${id}"]`).prop('checked', true);
      });
      this._updateTagFilterUI(selectedTagIds.size);
    });

    const tableOptions = {
      ...defaultTableOptions,
      serverSide: true,
      processing: true,
      // Preserve today's default ordering (server/DB order) on first load;
      // saved state still restores any user-applied sort.
      order: [],
      dom: 'rtp',
      columnDefs: [
        { targets: [0], orderable: false, width: '7%' },
        { targets: '_all', searchable: false }
      ],
      columns: this._getColumns($table),
      rowCallback: this._rowCallback,
      select: {
        style: 'multi',
        selector: 'label.js-checkbox-for-row',
        className: 'is-selected'
      },
      drawCallback() {
        customizePageLength(this);
        customizeDOMPaginationClasses();
        selection.onDraw(table);
        displayDatatablesPagination(this);
        TrackResponsesTable._renderCount($table, this.api().page.info());
      },
      ajax: {
        url: $table.data('url'),
        dataSrc: 'data',
        // Inject the server-side tag filter selection on every page request.
        data: params => ({ ...params, tag_ids: Array.from(selectedTagIds) })
      },
      initComplete: () => {
        setupPaginationOnDOM();
      },
      language: {
        ...defaultTableOptions.language,
        zeroRecords:
          '<span class="alert alert--info">Sorry, no matching Responses were found</span>',
        emptyTable: '<span class="alert alert--info">No Responses available</span>'
      }
    };
    const tableOptionsWithSaveState = withSaveState($table, tableOptions);
    table = $table.DataTable(tableOptionsWithSaveState);
    // Track manages selection (persisted across server-side pages) and export
    // (all matching rows) itself, so opt out of the shared current-page wiring.
    tableSetup(table, { selectAllButton: false, selectHooks: false, exportButtons: false });
  },

  watchRowClick($table) {
    const url = $table.data('detailsUrl');
    TableRowActive.attach($table.find('tbody'));
    $table.find('tbody').on('click', 'tr:not(.group)', (e) => {
      const listItemId = $(e.currentTarget).data('id');
      if (listItemId) {
        e.currentTarget.dataset.sidepanelHref = url.replace('LIST_ITEM_ID', listItemId);
        SidePanel.loadAndShow(e);
      }
    });
  },

  // Row selection that survives server-side page changes. The set of selected
  // list_item ids is the source of truth for the hidden bulk-action inputs and
  // the selection counter, so bulk actions cover every selected row across
  // pages. The header checkbox selects/deselects only the CURRENT page (the
  // historical behavior); selecting every matching row across pages is a
  // separate, explicit "Select all matching" affordance.
  _buildSelection($table) {
    const selectedIds = new Set();
    const $wrapper = $table.closest('.js-action-bar-flash-wrapper');
    const $hidden = $wrapper.find('.js-selected-rows-ids');
    const $counter = $wrapper.find('.js-items-selected-counter');
    const $flash = $wrapper.find('.flash');
    const $selectAllLink = $wrapper.find('.js-select-all-matching');
    const selectAllUrl = $table.data('selectAllUrl');
    let restoring = false;
    let allMatching = false;
    let previousSearch = '';
    let previousTags = '';

    const rowIds = data => data.toArray()
      .map(row => (row && row.attributes ? String(row.attributes.id) : null))
      .filter(Boolean);
    const idsFromIndexes = (table, indexes) => rowIds(table.rows(indexes).data());
    const currentPageIds = table => rowIds(table.rows({ page: 'current' }).data());

    const applyVisualSelection = (table) => {
      restoring = true;
      const matchIndexes = table.rows().indexes().filter((index) => {
        const data = table.row(index).data();
        return Boolean(data && data.attributes && selectedIds.has(String(data.attributes.id)));
      });
      table.rows(matchIndexes).select();
      restoring = false;
    };

    const render = (table) => {
      const ids = Array.from(selectedIds);
      const fieldName = $hidden.data('elementName');
      $hidden.html(
        ids.map(id => `<input type='hidden' name='${fieldName}[]' value='${id}'>`).join('')
      );
      const count = ids.length;
      if (count > 0) {
        $counter.text(allMatching
          ? I18n.t('datatables.selection.all_matching', { count })
          : I18n.t('datatables.selection.selected', { count }));
        $flash.trigger('selected-items:changed', [count]);
        ToggleVisible.show($flash);
      } else {
        $counter.text('');
        $flash.trigger('selected-items:changed', [0]);
        ToggleVisible.hide($flash);
      }
      // Offer "select all matching" only when a page-scoped selection exists and
      // more matching rows are available beyond what's selected.
      const totalMatching = table.page.info().recordsDisplay;
      if (!allMatching && count > 0 && totalMatching > count) {
        $selectAllLink
          .text(I18n.t('datatables.selection.select_all_matching', { count: totalMatching }))
          .removeClass('is-hidden');
      } else {
        $selectAllLink.addClass('is-hidden');
      }
      this._syncCheckAll(table, selectedIds);
    };

    const onDraw = (table) => {
      applyVisualSelection(table);
      render(table);
    };

    const clear = (table) => {
      selectedIds.clear();
      allMatching = false;
      restoring = true;
      table.rows().deselect();
      restoring = false;
      render(table);
    };

    const selectCurrentPage = (table) => {
      allMatching = false;
      currentPageIds(table).forEach(id => selectedIds.add(id));
      applyVisualSelection(table);
      render(table);
    };

    const deselectCurrentPage = (table) => {
      allMatching = false;
      currentPageIds(table).forEach(id => selectedIds.delete(id));
      restoring = true;
      table.rows({ page: 'current' }).deselect();
      restoring = false;
      render(table);
    };

    const selectAllMatching = (table) => {
      if (!selectAllUrl) return;
      const params = { ...table.ajax.params() };
      delete params.start;
      delete params.length;
      $.getJSON(selectAllUrl, params).done((response) => {
        selectedIds.clear();
        (response.ids || []).forEach(id => selectedIds.add(String(id)));
        allMatching = true;
        applyVisualSelection(table);
        render(table);
      });
    };

    const bind = (table) => {
      table.on('select', (e, dt, type, indexes) => {
        if (restoring) return;
        idsFromIndexes(table, indexes).forEach(id => selectedIds.add(id));
        allMatching = false;
        render(table);
      });
      table.on('deselect', (e, dt, type, indexes) => {
        if (restoring) return;
        idsFromIndexes(table, indexes).forEach(id => selectedIds.delete(id));
        allMatching = false;
        render(table);
      });
      // Header checkbox = current page only (never all matching).
      $('#checkall').on('change', (e) => {
        if (e.target.checked) {
          selectCurrentPage(table);
        } else {
          deselectCurrentPage(table);
        }
      });
      $('#uncheckall').on('click', (e) => {
        e.preventDefault();
        clear(table);
      });
      $selectAllLink.on('click', (e) => {
        e.preventDefault();
        selectAllMatching(table);
      });
      // Reset selection when the matching set changes (search or tag filter),
      // but preserve it across paging and sorting.
      table.on('preXhr.dt', (e, settings, data) => {
        const search = (data.search && data.search.value) || '';
        const tags = JSON.stringify(data.tag_ids || []);
        if (search !== previousSearch || tags !== previousTags) {
          selectedIds.clear();
          allMatching = false;
        }
        previousSearch = search;
        previousTags = tags;
      });
    };

    return { bind, onDraw, clear };
  },

  _syncCheckAll(table, selectedIds) {
    const pageRows = table.rows({ page: 'current' }).data().toArray();
    const allSelected = pageRows.length > 0 &&
      pageRows.every(row => row && row.attributes && selectedIds.has(String(row.attributes.id)));
    $('#checkall').prop('checked', allSelected);
  },

  // Copy / Print / CSV must cover every matching row, but under server-side
  // paging only the current page is in the DOM. Drive the export from the full
  // matching set instead (see exportAllMatchingRows).
  setupExport($table, table) {
    const url = $table.data('url');
    // Full column set (including the checkbox at index 0) so it aligns with the
    // cloned table header; the export selector excludes the checkbox/hidden cols.
    const exportColumns = this._getColumns($table);
    // Activity name as the heading title with its date/time as the subtitle; the
    // event name rides in the browser print header (docTitle). The report layout
    // is shared (see exportAllMatchingRows).
    const print = {
      docTitle: $table.data('printDocTitle'),
      title: $table.data('printTitle'),
      subtitle: $table.data('printSubtitle')
    };
    const triggerExport = type => (e) => {
      e.preventDefault();
      // Large events take several seconds to gather all matching rows; show a
      // progress dialog immediately and dismiss it once the export starts.
      ProgressDialog.show({
        title: I18n.t('export_buttons.preparing.title'),
        message: [
          I18n.t('export_buttons.preparing.gathering'),
          I18n.t('export_buttons.preparing.may_take')
        ],
        warning: I18n.t('export_buttons.preparing.do_not_navigate')
      });
      exportAllMatchingRows(table, { url, columns: exportColumns, type, print })
        .then(() => ProgressDialog.hide())
        .catch(() => {
          // Replacing the dialog dismisses the progress state and surfaces the
          // error in one transition.
          sweetAlert({
            title: I18n.t('export_buttons.failed.title'),
            text: I18n.t('export_buttons.failed.text'),
            icon: 'error'
          });
        });
    };
    $('.js-export-print').on('click', triggerExport('print'));
    $('.js-export-csv').on('click', triggerExport('csv'));
    $('.js-export-clipboard').on('click', triggerExport('copy'));
  },

  _getColumns($table) {
    /* eslint-disable no-param-reassign */
    const columns = $table.data('columns').map((column, index) => {
      if (index === 0) {
        column.render = columnRenders.check;
      } else if (column.data === 'attributes.tags') {
        column.type = 'tags';
        column.width = '15%';
        column.render = (data, type) => {
          const sorted = [...data].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
          if (type === 'sort') return sorted.map(t => t.name.toLowerCase()).join(', ');
          if (type === 'filter' || type === 'export') return sorted.map(t => t.name).join(', ');
          return sorted.map(t => `<span class="tag tag--${t.color}" style="color: ${t.textColor || 'inherit'}; font-weight: normal">${t.name}</span>`).join('');
        };
        // Print/CSV/Copy go through exportedContentFormat (table-setup.js),
        // which prefers cell.dataset.display over the rendered HTML. Set it
        // here so the exports get clean labels instead of chip markup.
        column.createdCell = (cell, cellData) => {
          const sorted = [...cellData].sort(
            (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          );
          cell.dataset.display = sorted.map(t => t.name).join(', ');
        };
      } else if (column.data === 'tracking.attending' || column.response_type === 'money') {
        column.render = { _: 'display', sort: 'order' };
      } else if (column.response_type === 'num') {
        column.type = 'num';
      } else if (column.response_type === 'boolean') {
        column.render = columnRenders.boolean;
      } else if (column.response_type === 'comment') {
        column.className = 'table__data--truncate has-truncate-text';
      } else if (
        column.response_type === 'date_and_time' &&
        (column.data && column.data.indexOf('tracking.') >= 0)
      ) {
        column.render = { _: 'display', sort: 'timestamp' };
      }
      column.render = column.render || $.fn.dataTable.render.text();
      return column;
    });
    return columns;
    /* eslint-enable no-param-reassign */
  },

  _rowCallback(row, data) {
    const listItemId = data.attributes.id;
    $(row).data('id', listItemId);
  }
};

export default TrackResponsesTable;
